// LiteLlmSettings — presentational analysis-provider picker, split out of
// LitePipelineSettings so the router-discovery/connection-test UX (9Router, OmniRoute,
// hosted providers, custom OpenAI-compatible) has its own file. Pure props-in: all
// localStorage writes stay in Lite.jsx via onProviderChange/onCfgChange/onGeminiKeyChange.
// Discovery + connection-test results are local component state only (never persisted).
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { CUSTOM_PRESET, getPreset, isLocalBase, discoverModels, testConnection, isAdvancedProvider } from '../../lib/llm/registry.js';
import { localRouterHint, LOCAL_NETWORK_PERMISSION_HINT, shouldShowLocalNetworkHint } from '../../lib/llm/chat.js';
import { ANALYZE_MODEL } from '../analyzeProxy.js';

const KEY_INPUT = 'w-full px-3 py-2.5 rounded-xl bg-cream-100 dark:bg-dark-400 border border-cream-200 dark:border-dark-200 text-sm font-mono text-dark-200 dark:text-white placeholder-cream-400 focus:ring-2 focus:ring-gold-500 outline-none';
const MINI_BTN = 'px-2.5 py-1.5 rounded-lg text-[11px] font-bold border border-cream-300 dark:border-dark-200 text-dark-200 dark:text-cream-200 hover:border-gold-500/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all';
const CHIP = 'px-2 py-0.5 rounded-full text-[10px] font-mono bg-cream-200 dark:bg-dark-300 text-dark-200 dark:text-cream-200 hover:bg-gold-500/20 hover:text-gold-600 cursor-pointer truncate max-w-[160px]';
const BADGE = 'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ring-1';

function portOf(url) {
  try { return new URL(url).port; } catch { return ''; }
}

export default function LiteLlmSettings({
  presets = [], providerId, onProviderChange, cfg, onCfgChange,
  geminiKey, onGeminiKeyChange, freeEnabled, busy,
  // gateAdvanced: Lite passes true, so local routers + the custom endpoint sit behind a
  // collapsed "Lanjutan" toggle. The full app's router panel leaves it false: that panel
  // is already behind its own "Lanjutan" section, and there the routers are the point.
  gateAdvanced = false,
}) {
  const [advOpen, setAdvOpen] = useState(false); // in-page toggle only, never persisted
  const [discovery, setDiscovery] = useState(null); // { models, usedDefaults } | null
  const [discovering, setDiscovering] = useState(false);
  const [conn, setConn] = useState(null);           // { ok, status, error } | null
  const [testing, setTesting] = useState(false);

  const preset = getPreset(providerId) || CUSTOM_PRESET;
  const values = cfg || {};
  const busyLock = !!busy || discovering || testing;
  const baseUrl = values.baseUrl || preset.baseUrl;
  const port = portOf(baseUrl);
  const showPortWarning = (providerId === 'omniroute' || providerId === '9router') && port === '20128';
  // A saved advanced choice stays visible (the <select> needs its option to exist);
  // otherwise the advanced options only appear after the user opens "Lanjutan".
  const selectedIsAdvanced = isAdvancedProvider(providerId);
  const showAdvanced = !gateAdvanced || advOpen || selectedIsAdvanced;
  const allOptions = [...presets, CUSTOM_PRESET];
  const basicOptions = allOptions.filter((p) => !isAdvancedProvider(p.id));
  const advancedOptions = allOptions.filter((p) => isAdvancedProvider(p.id));

  const runDiscover = async () => {
    setDiscovering(true);
    setDiscovery(null);
    try {
      const r = await discoverModels(preset, values.apiKey, baseUrl);
      setDiscovery(r);
    } finally {
      setDiscovering(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setConn(null);
    try {
      const model = values.model || preset.defaultModels.cheap;
      const r = await testConnection(preset, values.apiKey, baseUrl, model);
      setConn(r);
    } finally {
      setTesting(false);
    }
  };

  const connLabel = (() => {
    if (!conn) return null;
    if (conn.ok) return { text: 'OK', tone: 'ok' };
    if (conn.status === 401 || conn.status === 403) return { text: `HTTP ${conn.status}: key salah atau belum diisi`, tone: 'err' };
    if (conn.status === 0) {
      const hint = isLocalBase(baseUrl) ? localRouterHint(baseUrl) : (conn.error || '');
      return { text: `gak kejangkau${hint ? ' — ' + hint : ''}`, tone: 'err' };
    }
    return { text: `HTTP ${conn.status}`, tone: 'err' };
  })();

  return (
    <div>
      <label className="text-[11px] font-bold text-dark-200 dark:text-cream-200 flex items-center gap-1.5 mb-1.5"><span className="w-1.5 h-1.5 rounded-full bg-gold-300" /> Analisis AI</label>
      <select
        value={providerId}
        onChange={(e) => onProviderChange(e.target.value)}
        className={KEY_INPUT + ' mb-2'}
      >
        <option value="gemini">Gemini</option>
        {gateAdvanced ? (
          <>
            {basicOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            {showAdvanced && (
              <optgroup label="Lanjutan">
                {advancedOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </optgroup>
            )}
          </>
        ) : (
          <>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
            <option value={CUSTOM_PRESET.id}>{CUSTOM_PRESET.label}</option>
          </>
        )}
      </select>

      {gateAdvanced && !selectedIsAdvanced && (
        <div className="mb-2">
          <button
            type="button"
            onClick={() => setAdvOpen((v) => !v)}
            aria-expanded={advOpen}
            data-testid="llm-advanced-toggle"
            className="flex items-center gap-1 text-[11px] font-bold text-cream-500 dark:text-cream-400 hover:text-gold-600 dark:hover:text-gold-400 transition-colors"
          >
            <ChevronDown size={13} className={`transition-transform ${advOpen ? 'rotate-180' : ''}`} />
            Lanjutan
          </button>
          {advOpen && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="llm-advanced-options">
              {advancedOptions.map((p) => (
                <button key={p.id} type="button" onClick={() => onProviderChange(p.id)} disabled={!!busy} className={MINI_BTN}>
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {providerId === 'gemini' ? (
        <>
          <input type="password" value={geminiKey} placeholder="AIza..." onChange={(e) => onGeminiKeyChange(e.target.value)} className={KEY_INPUT} />
          <p className="text-[11px] text-emerald-600/80 dark:text-emerald-400/80 mt-1">Di aistudio.google.com/apikey · browser-direct, key gak lewat server kami (paling aman)</p>
        </>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="text-[11px] text-cream-500 dark:text-cream-400 block mb-1">Base URL</label>
            <input
              type="text"
              value={values.baseUrl ?? ''}
              placeholder={preset.baseUrl || 'https://...'}
              onChange={(e) => onCfgChange(providerId, { baseUrl: e.target.value })}
              className={KEY_INPUT}
            />
          </div>
          {shouldShowLocalNetworkHint(baseUrl, { conn, discovery }) && (
            <p className="text-[11px] text-sky-700 dark:text-sky-300 font-semibold">{LOCAL_NETWORK_PERMISSION_HINT}</p>
          )}
          {showPortWarning && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 font-semibold">OmniRoute dan 9Router sama-sama pakai :20128, jangan jalankan dua-duanya bareng</p>
          )}

          <div>
            <label className="text-[11px] text-cream-500 dark:text-cream-400 flex items-center gap-1.5 mb-1">
              <span>API key</span>
              {preset.id !== 'custom' && (
                <span className={`text-[10px] font-bold uppercase tracking-wide ${preset.requiresKey ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                  {preset.requiresKey ? 'wajib' : 'opsional'}
                </span>
              )}
            </label>
            <input
              type="password"
              value={values.apiKey ?? ''}
              placeholder={preset.requiresKey ? 'sk-...' : '(gak wajib — keyless)'}
              onChange={(e) => onCfgChange(providerId, { apiKey: e.target.value })}
              className={KEY_INPUT}
            />
            {preset.id === 'custom' && (
              <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-cream-500 dark:text-cream-400">
                <input
                  type="checkbox"
                  checked={values.requiresKey !== false}
                  onChange={(e) => onCfgChange(providerId, { requiresKey: e.target.checked })}
                />
                endpoint ini butuh key
              </label>
            )}
          </div>

          <div>
            <label className="text-[11px] text-cream-500 dark:text-cream-400 block mb-1">Model</label>
            <input
              type="text"
              list="lite-llm-model-options"
              value={values.model ?? ''}
              placeholder={preset.defaultModels?.cheap || '(default)'}
              onChange={(e) => onCfgChange(providerId, { model: e.target.value })}
              className={KEY_INPUT}
            />
            <datalist id="lite-llm-model-options">
              {(discovery?.models || []).map((id) => <option key={id} value={id} />)}
            </datalist>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="button" onClick={runDiscover} disabled={busyLock} className={MINI_BTN}>
              {discovering ? 'Cek model…' : 'Cek model'}
            </button>
            <button type="button" onClick={runTest} disabled={busyLock} className={MINI_BTN}>
              {testing ? 'Tes koneksi…' : 'Tes koneksi'}
            </button>
            {discovery?.usedDefaults && (
              <span className={`${BADGE} bg-amber-500/20 text-amber-600 dark:text-amber-400 ring-amber-500/30`}>
                discovery gagal, pakai default
              </span>
            )}
            {connLabel && (
              <span className={`${BADGE} ${connLabel.tone === 'ok' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 ring-emerald-500/30' : 'bg-red-500/20 text-red-600 dark:text-red-400 ring-red-500/30'}`}>
                {connLabel.text}
              </span>
            )}
          </div>

          {discovery?.models?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {discovery.models.slice(0, 30).map((id) => (
                <button
                  key={id}
                  type="button"
                  title={id}
                  onClick={() => onCfgChange(providerId, { model: id })}
                  className={CHIP}
                >
                  {id}
                </button>
              ))}
            </div>
          )}

          {preset.notes && <p className="text-[11px] text-cream-500 mt-1">{preset.notes}</p>}
          {preset.id === 'omniroute' && (
            <>
              <p className="text-[11px] text-cream-500 mt-1">OmniRoute wajib pakai API key: tanpa key, OmniRoute menolak permintaan dari browser (CORS). Bikin key di dashboard OmniRoute, menu API Keys.</p>
              <p className="text-[11px] text-cream-500 mt-1">Pilih model yang cepat (mis. gemini-3.1-flash-lite). Bawaan OmniRoute memutus permintaan di atas 15 detik (error 504), jadi model "thinking" yang lambat bisa gagal.</p>
            </>
          )}
          {preset.id === '9router' && (
            <>
              <p className="text-[11px] text-cream-500 mt-1">Key diambil dari dashboard 9Router di mesin kamu; model tergantung provider yang kamu sambungkan di dashboard.</p>
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">Catatan: 9Router versi sekarang (0.5.x) masih menolak pengecekan CORS dari browser, jadi belum bisa dipakai dari web. Pakai OmniRoute dulu.</p>
            </>
          )}
          {preset.id === 'openrouter' && freeEnabled && (
            <p className="text-[11px] text-cream-500 mt-1">Kosongin key OpenRouter juga bisa — jatuh ke server kami selama engine gratis nyala.</p>
          )}
          {/* Zero-key path rides the server's key, so bridge/qlipper_bridge.py pins the model
              (FREE_MODEL, same default as ANALYZE_MODEL) and ignores this form's Model field.
              Say so here, or "Tes koneksi" shows OK for a model that never actually runs.
              `!values.apiKey` mirrors chat.js, which sends X-Byok-Key only when apiKey is truthy. */}
          {preset.id === 'openrouter' && freeEnabled && !values.apiKey && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">Tanpa key, server selalu pakai model gratis {ANALYZE_MODEL}. Isian Model cuma kepake kalau kamu pakai key sendiri.</p>
          )}
          {!isLocalBase(preset.baseUrl) && preset.id !== 'openrouter' && preset.id !== 'custom' && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">Key wajib, gak ada jalur gratis buat provider ini.</p>
          )}
        </div>
      )}
    </div>
  );
}
